import { useEffect, useRef, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { countries } from "../data/countries.json";
import { API_URL } from "../config";

interface Photo {
  id: number;
  trip_id: number;
  photo_path: string;
  created_at: string;
  updated_at: string;
  url?: string;
}

interface Highlight {
  id: number;
  title: string;
}

interface Include {
  id: number;
  title: string;
}

interface Exclude {
  id: number;
  title: string;
}

interface Planning {
  id: number;
  day: string;
  planning: string;
}

interface IconProps {
  size?: number;
  color?: string;
}

const iconBase = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

const MapPinIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CalendarIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ListIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const XIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ImageIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const PlusIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SendIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const GripIcon: React.FC<IconProps> = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="9" cy="6" r="1.4" />
    <circle cx="15" cy="6" r="1.4" />
    <circle cx="9" cy="12" r="1.4" />
    <circle cx="15" cy="12" r="1.4" />
    <circle cx="9" cy="18" r="1.4" />
    <circle cx="15" cy="18" r="1.4" />
  </svg>
);

const TrashIcon: React.FC<IconProps> = ({ size = 15, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.328125 3.671875 14 4.5 14 L 10.5 14 C 11.328125 14 12 13.328125 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 5 5 L 6 5 L 6 12 L 5 12 Z M 7 5 L 8 5 L 8 12 L 7 12 Z M 9 5 L 10 5 L 10 12 L 9 12 Z" />
  </svg>
);

const UploadIcon: React.FC<IconProps> = ({ size = 28, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CheckCircleIcon: React.FC<IconProps> = ({ size = 8, color = "#10b981" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="12" cy="12" r="12" />
  </svg>
);

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  primaryTint: "#f0f4f6",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: colors.textMuted,
  marginBottom: 6,
};

const errorTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: colors.danger,
  marginTop: 4,
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: `0.5px solid ${colors.borderLight}`,
  borderRadius: 12,
};

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  error?: boolean;
  type?: string;
  placeholder?: string;
  prefix?: string;
  textarea?: boolean;
  rows?: number;
}

const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  required,
  error,
  type = "text",
  placeholder,
  prefix,
  textarea,
  rows = 4,
}) => {
  const [focused, setFocused] = useState(false);
  const borderColor = error ? colors.danger : focused ? colors.primary : colors.border;

  const fieldStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    padding: prefix ? "10px 12px 10px 48px" : "10px 12px",
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    fontSize: 13,
    color: colors.text,
    background: "#fff",
    outline: "none",
    fontFamily: "inherit",
    boxShadow: focused && !error ? `0 0 0 3px ${colors.primaryTint}` : "none",
    transition: "border-color .15s ease, box-shadow .15s ease",
  };

  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: colors.danger }}> *</span>}
      </label>
      <div style={{ position: "relative" }}>
        {prefix && (
          <span
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 13,
              color: colors.textFaint,
              pointerEvents: "none",
            }}
          >
            {prefix}
          </span>
        )}
        {textarea ? (
          <textarea
            rows={rows}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{ ...fieldStyle, resize: "vertical" }}
          />
        ) : (
          <input
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={fieldStyle}
          />
        )}
      </div>
      {error && <div style={errorTextStyle}>This field is required.</div>}
    </div>
  );
};

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  error?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  required,
  error,
}) => {
  const [focused, setFocused] = useState(false);
  const borderColor = error ? colors.danger : focused ? colors.primary : colors.border;

  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: colors.danger }}> *</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "10px 12px",
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          fontSize: 13,
          color: colors.text,
          background: "#fff",
          outline: "none",
          cursor: "pointer",
          boxShadow: focused && !error ? `0 0 0 3px ${colors.primaryTint}` : "none",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <div style={errorTextStyle}>This field is required.</div>}
    </div>
  );
};

interface SimpleListEditorProps {
  heading: string;
  addLabel: string;
  placeholder: string;
  items: { id?: number; name: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, value: string) => void;
  emptyHint: string;
}

const SimpleListEditor: React.FC<SimpleListEditorProps> = ({
  heading,
  addLabel,
  placeholder,
  items,
  onAdd,
  onRemove,
  onChange,
  emptyHint,
}) => (
  <div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{heading}</span>
      <button
        type="button"
        onClick={onAdd}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          background: colors.primaryTint,
          color: colors.primary,
          border: "none",
          borderRadius: 8,
          padding: "6px 10px",
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        <PlusIcon size={14} />
        {addLabel}
      </button>
    </div>

    {items.length === 0 ? (
      <div style={{ fontSize: 12, color: colors.textFaint, padding: "8px 2px" }}>{emptyHint}</div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px",
              background: colors.bgSubtle,
              borderRadius: 8,
            }}
          >
            <span style={{ color: colors.textFaint, display: "flex" }}>
              <GripIcon />
            </span>
            <input
              type="text"
              value={item.name}
              placeholder={placeholder}
              onChange={(e) => onChange(index, e.target.value)}
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                fontSize: 13,
                color: colors.text,
                outline: "none",
                padding: "4px 0",
                fontFamily: "inherit",
              }}
            />
            <button
              type="button"
              aria-label="Remove"
              onClick={() => onRemove(index)}
              style={{
                background: "none",
                border: "none",
                color: colors.textFaint,
                cursor: "pointer",
                padding: 4,
                display: "flex",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = colors.danger)}
              onMouseLeave={(e) => (e.currentTarget.style.color = colors.textFaint)}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const buttonBase: React.CSSProperties = {
  padding: "9px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid transparent",
};

const EditTour = () => {
  const { id } = useParams<{ id: string }>();
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [price, setPrice] = useState("");
  const [country, setCountry] = useState("");
  const [date_from, setDateFrom] = useState("");
  const [date_to, setDateTo] = useState("");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeletePhotoModal, setShowDeletePhotoModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [items, setItems] = useState<{ id?: number; name: string }[]>([]);
  const [items_includes, setItems_includes] = useState<{ id?: number; name: string }[]>([]);
  const [items_excludes, setItems_excludes] = useState<{ id?: number; name: string }[]>([]);
  const [items_planning, setItems_planning] = useState<{ id?: number; day: string; planning: string }[]>([]);

  const handleFiles = (selectedFiles: FileList | null) => {
    if (selectedFiles) {
      setFiles([...files, ...Array.from(selectedFiles)]);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const addItem = () => setItems([...items, { name: "" }]);
  const addItemIncludes = () => setItems_includes([...items_includes, { name: "" }]);
  const addItemExcludes = () => setItems_excludes([...items_excludes, { name: "" }]);
  const addItemPlanning = () => setItems_planning([...items_planning, { day: "", planning: "" }]);

  const removeItem = (index: number) => setItems(items.filter((_, i) => i !== index));
  const removeItemIncludes = (index: number) =>
    setItems_includes(items_includes.filter((_, i) => i !== index));
  const removeItemExcludes = (index: number) =>
    setItems_excludes(items_excludes.filter((_, i) => i !== index));
  const removeItemPlanning = (index: number) =>
    setItems_planning(items_planning.filter((_, i) => i !== index));

  const handleChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index].name = value;
    setItems(newItems);
  };

  const handleChangeIncludes = (index: number, value: string) => {
    const newItems = [...items_includes];
    newItems[index].name = value;
    setItems_includes(newItems);
  };

  const handleChangeExcludes = (index: number, value: string) => {
    const newItems = [...items_excludes];
    newItems[index].name = value;
    setItems_excludes(newItems);
  };

  const handleChangePlanning = (index: number, field: "day" | "planning", value: string) => {
    const newItems = [...items_planning];
    newItems[index][field] = value;
    setItems_planning(newItems);
  };

  useEffect(() => {
    if (!id) return;

    axios
      .get(`${API_URL}/trip/${id}`)
      .then((response) => {
        const trip = response.data;
        if (!trip) return;

        setTitle(trip.title || "");
        setCountry(trip.country || "");
        setLocation(trip.location || "");
        setDateFrom(trip.date_from || "");
        setDateTo(trip.date_to || "");
        setDescription(trip.description || "");
        setPrice(trip.price || "");

        const highlightsData = trip.highlights?.map((h: Highlight) => ({ id: h.id, name: h.title })) || [];
        setItems(highlightsData);

        const planningsData =
          trip.plannings?.map((p: Planning) => ({ id: p.id, day: p.day, planning: p.planning })) || [];
        setItems_planning(planningsData);

        const excludesData = trip.excludes?.map((e: Exclude) => ({ id: e.id, name: e.title })) || [];
        setItems_excludes(excludesData);

        const includesData = trip.includes?.map((i: Include) => ({ id: i.id, name: i.title })) || [];
        setItems_includes(includesData);

        const photosData =
          trip.photos?.map((p: Photo) => ({
            id: p.id,
            url: `${API_URL}/${p.photo_path}`,
          })) || [];

        setPhotos(photosData);
      })
      .catch((error) => {
        console.error("Error loading trip:", error);
      });
  }, [id]);

  const handleDeletePhotoClick = (photoId: number) => {
    setPhotoToDelete(photoId);
    setShowDeletePhotoModal(true);
  };

  const handleCancelDeletePhoto = () => {
    setShowDeletePhotoModal(false);
    setPhotoToDelete(null);
  };

  const handleConfirmDeletePhoto = async () => {
    if (photoToDelete === null) return;

    try {
      await axios.delete(`${API_URL}/photos/${photoToDelete}`);
      setPhotos(photos.filter((photo) => photo.id !== photoToDelete));
      toast.success("Photo deleted successfully");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Error deleting photo");
    } finally {
      setShowDeletePhotoModal(false);
      setPhotoToDelete(null);
    }
  };

  const handleRemoveNewPhoto = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const tripDetailsComplete =
    title.trim() !== "" &&
    description.trim() !== "" &&
    country.trim() !== "" &&
    location.trim() !== "" &&
    !!date_from &&
    !!date_to &&
    price.trim() !== "";
  const scheduleComplete = items_planning.some((i) => i.day.trim() !== "" || i.planning.trim() !== "");
  const includesComplete = items_includes.some((i) => i.name.trim() !== "");
  const excludesComplete = items_excludes.some((i) => i.name.trim() !== "");
  const picturesComplete = photos.length > 0 || files.length > 0;

  const tabs = [
    { label: "Trip details", Icon: MapPinIcon, complete: tripDetailsComplete },
    { label: "Schedule", Icon: CalendarIcon, complete: scheduleComplete },
    { label: "Inclusions", Icon: ListIcon, complete: includesComplete },
    { label: "Exclusions", Icon: XIcon, complete: excludesComplete },
    { label: "Pictures", Icon: ImageIcon, complete: picturesComplete },
  ];

  const titleError = attemptedSubmit && !title.trim();
  const descriptionError = attemptedSubmit && !description.trim();
  const countryError = attemptedSubmit && !country.trim();
  const locationError = attemptedSubmit && !location.trim();
  const dateFromError = attemptedSubmit && !date_from;
  const dateToError = attemptedSubmit && !date_to;
  const priceError = attemptedSubmit && !price.trim();

  const handleSubmit = async () => {
    setAttemptedSubmit(true);

    if (!title.trim() || !description.trim() || !country.trim() || !location.trim() || !price.trim()) {
      setTabIndex(0);
      toast.error("Please fill in all required trip details.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("country", country);
      formData.append("location", location);
      formData.append("date_from", date_from);
      formData.append("date_to", date_to);

      files.forEach((file) => {
        formData.append("photos[]", file);
      });

      items.forEach((item) => {
        if (item.name.trim() !== "") {
          formData.append("highlights[]", item.name);
        }
      });

      items_includes.forEach((item) => {
        if (item.name.trim() !== "") {
          formData.append("includes[]", item.name);
        }
      });

      items_excludes.forEach((item) => {
        if (item.name.trim() !== "") {
          formData.append("excludes[]", item.name);
        }
      });

      items_planning.forEach((item) => {
        if (item.day.trim() !== "" || item.planning.trim() !== "") {
          formData.append("planning[]", JSON.stringify(item));
        }
      });

      const response = await axios.post(`${API_URL}/trip/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/admin/list-trips");
      toast.success("Trip updated successfully!");
      console.log(response.data);
    } catch (error: any) {
      console.error("Error updating trip:", error.response?.data || error.message);
      toast.error("Error updating trip");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        {/* Structure Grille : Left (20%), Center (60%), Right (20%) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "20% 60% 20%",
            gap: "16px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Section Gauche (20%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ ...cardStyle, padding: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 8px 0" }}>Edit tour</h2>
              <div style={{ fontSize: 12, color: colors.textMuted }}>ID: #{id}</div>
            </div>
          </div>

          {/* Section Centre (60%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
                <TabList
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    padding: "8px 10px 0",
                    margin: 0,
                    borderBottom: `0.5px solid ${colors.borderLight}`,
                    listStyle: "none",
                  }}
                >
                  {tabs.map((t, index) => {
                    const active = tabIndex === index;
                    return (
                      <Tab key={t.label} style={{ outline: "none" }}>
                        <button
                          type="button"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "10px 12px",
                            fontSize: 12,
                            fontWeight: 500,
                            color: active ? colors.primary : colors.textFaint,
                            background: "none",
                            border: "none",
                            borderBottom: active ? `2px solid ${colors.primary}` : "2px solid transparent",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <t.Icon size={14} />
                          {t.label}
                          {t.complete && <CheckCircleIcon />}
                        </button>
                      </Tab>
                    );
                  })}
                </TabList>

                {/* Trip Details Tab */}
                <TabPanel style={{ display: tabIndex === 0 ? "block" : "none", padding: 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <TextField
                      label="Title"
                      value={title}
                      onChange={setTitle}
                      required
                      error={titleError}
                      placeholder="Tour title"
                    />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <SelectField
                        label="Country"
                        value={country}
                        onChange={setCountry}
                        required
                        error={countryError}
                        options={[
                          { value: "", label: "Select country" },
                          ...countries.map((c: string) => ({ value: c, label: c })),
                        ]}
                      />
                      <TextField
                        label="Location"
                        value={location}
                        onChange={setLocation}
                        required
                        error={locationError}
                        placeholder="Location"
                      />
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <TextField
                        label="Date from"
                        value={date_from}
                        onChange={setDateFrom}
                        required
                        error={dateFromError}
                        type="date"
                      />
                      <TextField
                        label="Date to"
                        value={date_to}
                        onChange={setDateTo}
                        required
                        error={dateToError}
                        type="date"
                      />
                      <TextField
                        label="Price"
                        value={price}
                        onChange={setPrice}
                        required
                        error={priceError}
                        placeholder="0.000"
                        prefix="OMR"
                        type="text"
                      />
                    </div>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={setDescription}
                      required
                      error={descriptionError}
                      placeholder="Description"
                      textarea
                      rows={4}
                    />
                  </div>

                  <div style={{ borderTop: `0.5px solid ${colors.borderLight}`, marginTop: 16, paddingTop: 16 }}>
                    <SimpleListEditor
                      heading="Tour highlights"
                      addLabel="Add"
                      placeholder="Tour highlight"
                      items={items}
                      onAdd={addItem}
                      onRemove={removeItem}
                      onChange={handleChange}
                      emptyHint="No highlights added yet."
                    />
                  </div>
                </TabPanel>

                {/* Schedule Tab */}
                <TabPanel style={{ display: tabIndex === 1 ? "block" : "none", padding: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>Trip planning</span>
                    <button
                      type="button"
                      onClick={addItemPlanning}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        background: colors.primaryTint,
                        color: colors.primary,
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      <PlusIcon size={14} />
                      Add day
                    </button>
                  </div>

                  {items_planning.length === 0 ? (
                    <div style={{ fontSize: 12, color: colors.textFaint, padding: "8px 2px" }}>
                      No day-by-day planning added yet.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {items_planning.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 8,
                            padding: 10,
                            background: colors.bgSubtle,
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ width: 80 }}>
                            <label style={{ ...labelStyle, fontSize: 11 }}>Day</label>
                            <input
                              type="text"
                              value={item.day}
                              onChange={(e) => handleChangePlanning(index, "day", e.target.value)}
                              placeholder="Day 1"
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                padding: "6px 8px",
                                border: `1px solid ${colors.border}`,
                                borderRadius: 6,
                                fontSize: 12,
                                background: "#fff",
                                outline: "none",
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ ...labelStyle, fontSize: 11 }}>Planning</label>
                            <input
                              type="text"
                              value={item.planning}
                              onChange={(e) => handleChangePlanning(index, "planning", e.target.value)}
                              placeholder="Planning details"
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                padding: "6px 8px",
                                border: `1px solid ${colors.border}`,
                                borderRadius: 6,
                                fontSize: 12,
                                background: "#fff",
                                outline: "none",
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            aria-label="Remove"
                            onClick={() => removeItemPlanning(index)}
                            style={{
                              background: "none",
                              border: "none",
                              color: colors.textFaint,
                              cursor: "pointer",
                              padding: 6,
                              display: "flex",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = colors.danger)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = colors.textFaint)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabPanel>

                {/* Inclusions Tab */}
                <TabPanel style={{ display: tabIndex === 2 ? "block" : "none", padding: 16 }}>
                  <SimpleListEditor
                    heading="Inclusions"
                    addLabel="Add"
                    placeholder="Inclusion"
                    items={items_includes}
                    onAdd={addItemIncludes}
                    onRemove={removeItemIncludes}
                    onChange={handleChangeIncludes}
                    emptyHint="No inclusions added yet."
                  />
                </TabPanel>

                {/* Exclusions Tab */}
                <TabPanel style={{ display: tabIndex === 3 ? "block" : "none", padding: 16 }}>
                  <SimpleListEditor
                    heading="Exclusions"
                    addLabel="Add"
                    placeholder="Exclusion"
                    items={items_excludes}
                    onAdd={addItemExcludes}
                    onRemove={removeItemExcludes}
                    onChange={handleChangeExcludes}
                    emptyHint="No exclusions added yet."
                  />
                </TabPanel>

                {/* Pictures Tab */}
                <TabPanel style={{ display: tabIndex === 4 ? "block" : "none", padding: 16 }}>
                  <div
                    onClick={handleClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: `1.5px dashed ${dragActive ? colors.primary : "#c1c9d0"}`,
                      background: dragActive ? colors.primaryTint : colors.bgSubtle,
                      borderRadius: 10,
                      padding: "24px 16px",
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "background .15s ease, border-color .15s ease",
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={inputRef}
                      style={{ display: "none" }}
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                    <div style={{ display: "flex", justifyContent: "center", color: colors.primary, marginBottom: 8 }}>
                      <UploadIcon size={24} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>
                      Drop photos here or click to upload
                    </div>
                    <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 4 }}>
                      JPG or PNG, up to 5MB per file.
                    </div>
                  </div>

                  {/* Photos existantes */}
                  {photos.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: colors.text, marginBottom: 8 }}>
                        Existing photos ({photos.length})
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {photos.map((photo) => (
                          <div
                            key={photo.id}
                            className="tour-thumb"
                            style={{
                              width: 70,
                              height: 70,
                              borderRadius: 8,
                              overflow: "hidden",
                              position: "relative",
                              border: `1px solid ${colors.borderLight}`,
                            }}
                          >
                            <img
                              src={photo.url}
                              alt={`Photo ${photo.id}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                            <button
                              type="button"
                              className="remove-btn"
                              aria-label="Delete photo"
                              onClick={() => handleDeletePhotoClick(photo.id)}
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                background: colors.danger,
                                color: "#fff",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <XIcon size={10} color="#fff" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Aperçu des nouvelles images */}
                  {files.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: colors.text, marginBottom: 8 }}>
                        New photos to add ({files.length})
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            className="tour-thumb"
                            style={{
                              width: 70,
                              height: 70,
                              borderRadius: 8,
                              overflow: "hidden",
                              position: "relative",
                              border: `1px solid ${colors.borderLight}`,
                            }}
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                            <button
                              type="button"
                              className="remove-btn"
                              aria-label={`Remove ${file.name}`}
                              onClick={() => handleRemoveNewPhoto(idx)}
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                background: colors.primary,
                                color: "#fff",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <XIcon size={10} color="#fff" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabPanel>
              </Tabs>
            </div>
          </div>

          {/* Section Droite (20%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                ...cardStyle,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0, color: colors.text }}>Actions</h3>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                style={{
                  ...buttonBase,
                  background: colors.primary,
                  color: "#fff",
                  opacity: isLoading ? 0.6 : 1,
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.background = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.background = colors.primary;
                }}
              >
                <SendIcon size={14} color="#fff" />
                {isLoading ? "Updating..." : "Update"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/admin/list-trips")}
                style={{
                  ...buttonBase,
                  background: "#fff",
                  borderColor: colors.border,
                  color: colors.textMuted,
                  width: "100%",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation pour la suppression de photo */}
      {showDeletePhotoModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "420px",
              maxWidth: "90%",
              overflow: "hidden",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <h5 style={{ fontSize: 15, fontWeight: 600, color: colors.text, margin: 0 }}>
                Confirm photo deletion
              </h5>
              <button
                type="button"
                onClick={handleCancelDeletePhoto}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: colors.textFaint,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px", fontSize: 13, color: colors.textMuted }}>
              Are you sure you want to delete this photo? This action cannot be undone.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                padding: "12px 20px",
                borderTop: `1px solid ${colors.borderLight}`,
                background: colors.bgSubtle,
              }}
            >
              <button
                type="button"
                onClick={handleCancelDeletePhoto}
                style={{
                  ...buttonBase,
                  background: "#fff",
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeletePhoto}
                style={{
                  ...buttonBase,
                  background: colors.danger,
                  color: "#fff",
                }}
              >
                Delete photo
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .tour-thumb .remove-btn {
            opacity: 0;
            transition: opacity .15s ease;
          }
          .tour-thumb:hover .remove-btn {
            opacity: 1;
          }
        `}
      </style>
      <ToastContainer />
    </>
  );
};

export default EditTour;